import React, { useCallback, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { syncUserFromServer } from '../../store/slices/authSlice';
import { useMediaQuery } from 'react-responsive';
import WMSSidebar from './WMSSidebar';
import WMSNavbar from './WMSNavbar';
import WMSFooter from './WMSFooter';
import SupportWidget from '../../components/common/SupportWidget';
import LiveToastListener from '../../components/notifications/LiveToastListener';

/**
 * WMSLayout owns the mobile-drawer open state so both the navbar's
 * hamburger (opens it) and the sidebar itself (closes on nav + route
 * change) can drive one value. Desktop layouts bypass the whole
 * thing — on viewports >=1200px the sidebar stays pinned and the
 * hamburger is hidden. On 1199px and below (covers phones, tablets,
 * and small laptops) the drawer takes over.
 */
const WMSLayout = () => {
  const location = useLocation();
  // Match the legacy Sofbox responsive.css break exactly: that file
  // hides the pinned sidebar (``left: -260px``) at <=1299px and only
  // restores it when ``body.sidebar-main`` is set — we don't set
  // that class. So anything <=1299px must use the React drawer,
  // otherwise the 1200-1299 band (common on Windows laptops at
  // 125-150% display scaling) gets no sidebar AND no hamburger.
  const isMobile = useMediaQuery({ maxWidth: 1299 });

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const closeSidebar = useCallback(() => setSidebarOpen(false), []);
  const toggleSidebar = useCallback(() => setSidebarOpen((v) => !v), []);

  // Snap back to closed when the viewport class changes — on resize
  // to desktop the drawer state becomes meaningless, and reopening
  // the page on mobile should never start with it open.
  useEffect(() => { setSidebarOpen(false); }, [isMobile]);

  // Close on route change (tapping a menu item on mobile should hide
  // the drawer immediately so the content is visible).
  useEffect(() => {
    if (isMobile) setSidebarOpen(false);
  }, [location.pathname, isMobile]);

  // Pick up server-side profile changes (an admin changing this user's
  // role) on load and whenever the tab regains focus, at most every 5 min —
  // otherwise the menu kept showing the old role until logout.
  const dispatch = useDispatch();
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    let last = 0;
    const sync = () => {
      const now = Date.now();
      if (now - last < 5 * 60 * 1000) return;
      last = now;
      dispatch(syncUserFromServer());
    };
    sync();
    window.addEventListener('focus', sync);
    return () => window.removeEventListener('focus', sync);
  }, [dispatch, isAuthenticated]);

  useEffect(() => {
    document.body.classList.add('sidebar-main-active');
    return () => document.body.classList.remove('sidebar-main-active');
  }, []);

  // Prevent the content underneath from scrolling while the drawer is
  // open on mobile — a common iOS footgun.
  useEffect(() => {
    if (!isMobile) return undefined;
    if (sidebarOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [sidebarOpen, isMobile]);

  return (
    <>
      <div className="wrapper">
        <WMSSidebar
          isMobile={isMobile}
          mobileOpen={sidebarOpen}
          onClose={closeSidebar}
        />
        {/* Backdrop — mobile-only, rendered when the drawer is open.
            Using an actual <button> keeps the a11y story honest
            (keyboard focusable, dismissable with Enter/Space). */}
        {isMobile && sidebarOpen ? (
          <button
            type="button"
            className="ws-sidebar-backdrop"
            aria-label="Close navigation"
            onClick={closeSidebar}
          />
        ) : null}
        <WMSNavbar
          showHamburger={isMobile}
          onMenuToggle={toggleSidebar}
        />
        <div id="content-page" className="content-page">
          <div className="container-fluid">
            <Outlet />
          </div>
        </div>
      </div>
      {/* Phase 18c — pops a toast for each incoming WS notification.
          Zero-render component; lives outside the main flow. */}
      <LiveToastListener />
      <SupportWidget />
      <WMSFooter />
    </>
  );
};

export default WMSLayout;
